import FeaturedArticle from 'components/ArticleGrid/LargeFeaturedArticle';
import { cn, getColorway, getYMargins } from 'utils/styles';
import SmallArticle from 'components/ArticleGrid/SmallArticle';
import { ArticleGridProps, RelatedArticleProps } from './types';
import ArticleRowCarousel from 'components/ArticleGrid/ArticleRowCarousel';
import ArticleGridCarousel from 'components/ArticleGrid/ArticleGridCarousel';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import Button from 'components/shared/Buttons/BaseButton';
import { MediumArticleDisplayVariant } from 'components/ArticleGrid/MediumArticle';
import MediumArticleGrid, { MediumArticleGridLayout } from 'components/ArticleGrid/MediumArticleGrid';
import ArticleGridPaginated from 'components/ArticleGrid/ArticleGridPaginated';

import { liveUpdateAttrs } from 'libs/contentful/utils';
import {
  fetchEntriesByTaxonomyREST,
  adaptEntriesForArticleGrid,
  ArticleGridItem,
  extractEntryConceptIds,
  fetchConceptDetails,
  type ResolvedConcept,
} from 'libs/contentful/rest';
import { isDraftPreviewEnabled } from 'utils/draftPreview';
import { getPageContext } from 'utils/pageContext';
import { resourceToArticleShim } from './utils';
import { getMostSpecificConcept } from 'utils/taxonomy';

interface TaxonomyFetchResult {
  articles: ArticleGridItem[];
  /** Map of entry sys.id to its tagged concept IDs */
  entryConceptIds: Map<string, string[]>;
  /** Map of concept ID to resolved concept details (label + hierarchy) */
  concepts: Map<string, ResolvedConcept>;
}

/**
 * Fetches articles by taxonomy ID and resolves concept labels for category display.
 * Supports PageBlogPostFlexible, BlogPostPage (blogPage), and Resource content types.
 */
async function fetchArticlesByTaxonomy(taxonomyId: string, subdomain?: string): Promise<TaxonomyFetchResult> {
  const draftPreview = await isDraftPreviewEnabled();
  const { entries, includes } = await fetchEntriesByTaxonomyREST(taxonomyId, draftPreview, { subdomain });

  if (entries.length === 0) {
    return { articles: [], entryConceptIds: new Map(), concepts: new Map() };
  }

  const articles = adaptEntriesForArticleGrid(entries, includes);

  // Build a map of entry ID -> concept IDs from the raw entries
  const entryConceptIds = extractEntryConceptIds(entries);

  // Fetch concept details (labels + broader info) for all tagged concepts
  const allConceptIds = [...entryConceptIds.values()].flat();
  const concepts = await fetchConceptDetails(allConceptIds);

  return { articles, entryConceptIds, concepts };
}

export enum ArticleGridVariant {
  Article = 'Article',
  Featured = 'Featured',
  Explore = 'Explore',
  ShortCta = 'Short CTA',
  Grid = 'Grid',
  Paginated = 'Paginated',
}

/**
 * Props for the internal ArticleGridContent component.
 * Includes pre-fetched taxonomy articles if applicable.
 */
interface ArticleGridContentProps extends ArticleGridProps {
  /** Pre-fetched articles from taxonomy (already shimmed) */
  taxonomyArticles?: RelatedArticleProps[];
}

/**
 * Internal synchronous content component for ArticleGrid.
 */
const ArticleGridContent = ({
  variant,
  sectionName,
  color,
  margins,
  featuredArticleColor,
  articlesCollection,
  headline,
  cta,
  pullFromTaxonomy,
  taxonomyArticles = [],
  useSolidCardBackgrounds,
  maxArticles,
  articlesPerPage,
  articlesPerPageMobile,
  sys,
  showDates,
  includeCategoryLinks,
}: ArticleGridContentProps) => {
  // When taxonomy mode is active, use only taxonomy articles (no fallback to articlesCollection)
  // This ensures the component renders nothing when taxonomy is selected but returns no content
  const articles =
    pullFromTaxonomy?.value && pullFromTaxonomy?.label
      ? taxonomyArticles
      : (articlesCollection?.items ?? []).map(article => resourceToArticleShim(article));

  const useArticleGridCarouselLayout = variant === ArticleGridVariant.Explore;
  const useArticleLayout = variant === ArticleGridVariant.Article;
  const useFeaturedLayout = variant === ArticleGridVariant.Featured;
  const useShortCtaLayout = variant === ArticleGridVariant.ShortCta;
  const useGridLayout = variant === ArticleGridVariant.Grid;
  const usePaginatedLayout = variant === ArticleGridVariant.Paginated;

  const articlesSlice = articles.slice(0, maxArticles ?? 3);

  if (!articles.length) return null;

  const mediumArticleDisplayVariant = useSolidCardBackgrounds
    ? MediumArticleDisplayVariant.Card
    : MediumArticleDisplayVariant.Default;
  // articlesPerPageMobile=3: show all articles in a stacked column (mobile) / row (sm+).
  // Otherwise: show articles in a swipeable carousel on mobile, static row on sm+.
  const mediumArticleLayout =
    articlesPerPageMobile === 3 ? MediumArticleGridLayout.Flex : MediumArticleGridLayout.Carousel;

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={cn(getColorway(color), getYMargins(margins))}>
      <div className="content-wrapper flex flex-col">
        {useGridLayout && (
          <>
            {headline && (
              <div
                className="font-sans font-semibold text-heading-md md:text-heading-lg mb-12"
                {...liveUpdateAttrs(sys.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            )}
            <ArticleGridPaginated
              articlesPerPage={articlesPerPage}
              gridClassName="grid grid-flow-col [grid-template-rows:repeat(3,auto)] auto-cols-[85%] sm:auto-cols-[43%] md:auto-cols-[44%] ml:auto-cols-[38%] lg:auto-cols-[31%] gap-x-8 gap-y-8"
              paginationType="carousel">
              {articles.map((article, i) => (
                <SmallArticle
                  {...article}
                  articleImage={undefined}
                  includeCategoryLinks={includeCategoryLinks}
                  key={i}
                />
              ))}
            </ArticleGridPaginated>
          </>
        )}

        {useArticleLayout && (
          <>
            {headline ? (
              <div
                className="font-sans font-semibold text-heading-md md:text-heading-lg mb-12"
                {...liveUpdateAttrs(sys.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            ) : (
              <h2 className="sr-only">Featured articles</h2>
            )}
            <MediumArticleGrid
              articles={articlesSlice}
              layout={mediumArticleLayout}
              displayVariant={mediumArticleDisplayVariant}
              color={color}
              showDates={showDates}
              includeCategoryLinks={includeCategoryLinks}
              shouldCycleColors={useSolidCardBackgrounds}
            />
          </>
        )}

        {useFeaturedLayout && (
          <div className={cn('grid md:grid-cols-3 grid-cols-1 grid-rows-1 gap-8')}>
            <div className="col-span-2">
              <FeaturedArticle {...articles[0]} featuredArticleColor={featuredArticleColor} />
            </div>

            <div className="grid grid-rows-3 gap-12 items-center">
              {articles.slice(1, 4).map((article, i) => {
                return <SmallArticle {...article} includeCategoryLinks={includeCategoryLinks} key={i} />;
              })}
            </div>
          </div>
        )}

        {useArticleGridCarouselLayout && (
          <ArticleGridCarousel color={color} articles={articles} includeCategoryLinks={includeCategoryLinks} />
        )}

        {useShortCtaLayout && (
          <div className={'flex flex-col md:flex-row md:justify-between gap-8'}>
            <div className={'flex flex-col w-[314px] gap-5'}>
              {headline && (
                <div
                  className="font-sans font-semibold text-heading-md md:text-heading-lg"
                  {...liveUpdateAttrs(sys.id, 'headline')}>
                  <RichTextHeadline headline={headline} />
                </div>
              )}
              {cta && (
                <div className="hidden md:block max-w-max">
                  <Button href={cta?.url} sys={cta?.sys} internalPage={cta?.internalPage} variant={'outline'}>
                    {cta?.text}
                  </Button>
                </div>
              )}
            </div>
            <div className={'max-w-[990px]'}>
              <ArticleRowCarousel color={color} totalItems={articlesSlice.length}>
                {articlesSlice.map((article, i) => (
                  <SmallArticle {...article} includeCategoryLinks={includeCategoryLinks} key={i} />
                ))}
              </ArticleRowCarousel>
            </div>

            {cta && (
              <div className="block md:hidden max-w-max">
                <Button href={cta?.url} sys={cta?.sys} internalPage={cta?.internalPage} variant={'outline'}>
                  {cta?.text}
                </Button>
              </div>
            )}
          </div>
        )}

        {usePaginatedLayout && (
          <>
            {headline && (
              <div
                className="font-sans font-semibold text-heading-md md:text-heading-lg mb-12"
                {...liveUpdateAttrs(sys.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            )}
            <MediumArticleGrid
              articles={articles}
              layout={MediumArticleGridLayout.Paginated}
              displayVariant={mediumArticleDisplayVariant}
              color={color}
              showDates={showDates}
              includeCategoryLinks={includeCategoryLinks}
              articlesPerPage={articlesPerPage}
              shouldCycleColors={false}
            />
          </>
        )}
      </div>
    </section>
  );
};

/**
 * Async Server Component that fetches taxonomy articles and renders the grid.
 * Supports fetching PageBlogPostFlexible, BlogPostPage, and Resource entries by taxonomy.
 */
const ArticleGrid = async (props: ArticleGridProps) => {
  let taxonomyArticles: RelatedArticleProps[] = [];

  // Fetch articles by taxonomy and resolve per-article concept labels
  if (props.pullFromTaxonomy?.value && props.pullFromTaxonomy?.label) {
    const { subdomain } = await getPageContext();
    const { articles, entryConceptIds, concepts } = await fetchArticlesByTaxonomy(
      props.pullFromTaxonomy.value,
      subdomain,
    );
    const fallbackLabel = props.pullFromTaxonomy.label;

    taxonomyArticles = articles.map(article => {
      const articleId = article.sys?.id ?? '';
      const conceptIds = entryConceptIds.get(articleId) ?? [];
      const concept = getMostSpecificConcept(conceptIds, concepts);
      const label = concept?.label ?? fallbackLabel;
      return resourceToArticleShim(article, label, concept?.uri);
    });
  }

  return <ArticleGridContent {...props} taxonomyArticles={taxonomyArticles} />;
};

export default ArticleGrid;
